// The learner's video player, and the client half of the watch gate.
//
// The server is the authority on what counts as watched; this component exists
// so the interface never lets someone do something the server will reject. It
// reports the spans actually played, refuses to seek past the frontier the
// server returned, and resumes where the learner left off.
//
// Only a lesson's own video blocks are watched in that sense. A video in a
// homework brief or beside a practice plays like any other: nothing is
// reported, nothing is locked (`trackProgress` false, or no block id).

import { useCallback, useEffect, useRef, useState } from 'react'
import { get, post } from '../api'
import type { Playback, ProgressReport } from '../api'
import { useT } from '../i18n'
import learn from '../i18n/learn'

/** How much media time to accumulate before telling the server. */
const REPORT_EVERY_SECONDS = 10

/** Matches domain.SeekToleranceSeconds on the server. */
const SEEK_TOLERANCE = 2

/** Matches the resume offset the server records; a few seconds of run-up helps. */
const RESUME_REWIND = 3

type Props = {
  /** The lesson block the video is in; progress is reported against it. */
  blockId?: string
  videoId: string
  /** Where the learner stopped last time, in seconds. */
  initialPosition?: number
  /** How far they are allowed to seek, in seconds. */
  initialFrontier?: number
  preventSeeking?: boolean
  /**
   * Whether watching counts towards the lesson (default true). Off, the
   * player reports nothing and lets the learner seek anywhere.
   */
  trackProgress?: boolean
  poster?: string
  onProgress?: (p: ProgressReport) => void
  onCompleted?: () => void
}

export function VideoPlayer({
  blockId,
  videoId,
  initialPosition = 0,
  initialFrontier = 0,
  preventSeeking: preventSeekingWanted = false,
  trackProgress = true,
  poster,
  onProgress,
  onCompleted,
}: Props) {
  const t = useT(learn)
  // Watch time is the lesson's business only for a lesson block; without one
  // there is nothing to report against and nothing to hold back.
  const tracking = trackProgress && Boolean(blockId)
  const preventSeeking = tracking && preventSeekingWanted
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // Which failure, not its words: the words follow the language on screen,
  // including a switch made after the failure.
  const [error, setError] = useState<'load' | 'unsupported' | null>(null)
  const [duration, setDuration] = useState(0)
  // The rail redraws from state; the ref above is what the gate reads.
  const [unlocked, setUnlocked] = useState(initialFrontier)

  // Refs, not state: these are read inside event handlers that must not be
  // re-bound on every change, and a stale closure here would lose watch time.
  const frontier = useRef(initialFrontier)
  const spanStart = useRef<number | null>(null)
  const completed = useRef(false)
  const snappingBack = useRef(false)
  const reporting = useRef(false)

  /** Sends [from,to] and adopts whatever the server says the truth now is. */
  const report = useCallback(
    async (from: number, to: number, position: number) => {
      if (!tracking || !blockId) return
      if (to - from < 0.5) return
      // Overlapping reports are harmless server-side, but serialising them keeps
      // the frontier we hold from going backwards under a slow response.
      if (reporting.current) return
      reporting.current = true
      try {
        const res = await post<ProgressReport>('/learn/progress/report', {
          content_block_id: blockId,
          from,
          to,
          position,
        })
        // Never backwards. The server's frontier is up to one reporting
        // interval behind what has just been played, and adopting it verbatim
        // would strand the learner mid-video: the local frontier would sit
        // below the playhead, out of reach of the bounded advance in
        // onTimeUpdate, and the next seek would throw away watch time they
        // genuinely earned. The local value only ever grows through contiguous
        // playback, so taking the larger of the two is honest.
        frontier.current = Math.max(frontier.current, res.frontier)
        setUnlocked(frontier.current)
        onProgress?.(res)
        if (res.completed && !completed.current) {
          completed.current = true
          onCompleted?.()
        }
      } catch {
        // A dropped report costs a few seconds of credit and will be made up by
        // the next one. Failing the playback over it would be worse.
      } finally {
        reporting.current = false
      }
    },
    [tracking, blockId, onProgress, onCompleted],
  )

  /** Reports whatever has accumulated and starts a fresh span. */
  const flush = useCallback(
    (video: HTMLVideoElement) => {
      const from = spanStart.current
      if (from === null) return
      const to = video.currentTime
      spanStart.current = to
      if (to > from) void report(from, to, to)
    },
    [report],
  )

  // ---------------------------------------------------------------- source --

  useEffect(() => {
    let cancelled = false
    const video = videoRef.current
    if (!video) return

    // Kept alive for the lifetime of the source so it can be torn down.
    let hls: { destroy(): void } | null = null

    ;(async () => {
      let playback: Playback
      try {
        playback = await get<Playback>(`/learn/videos/${videoId}/playback`)
      } catch {
        if (!cancelled) setError('load')
        return
      }
      if (cancelled) return
      setDuration(playback.duration_seconds)

      // Ask the polyfill first, not the browser. Chromium answers "maybe" to
      // canPlayType for HLS and then fails to demux it, so trusting that check
      // hands most of our learners a broken player. Where Media Source
      // Extensions exist, hls.js is the answer; Safari, which has no MSE for
      // this but does play HLS natively, falls through to the element.
      const { default: Hls } = await import('hls.js')
      if (cancelled) return

      if (Hls.isSupported()) {
        const instance = new Hls({ enableWorker: true, lowLatencyMode: false })
        instance.loadSource(playback.url)
        instance.attachMedia(video)

        // hls.js handles the ordinary stumbles itself. Even a "fatal" one is
        // usually recoverable — a dropped segment request, a demuxer that lost
        // its place after a seek — and giving up on the first would replace a
        // working lesson with an error message over a hiccup. Recovery is
        // attempted once per kind; a second failure is a real failure.
        let recovered = { network: false, media: false }
        instance.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !recovered.network) {
            recovered.network = true
            instance.startLoad()
            return
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recovered.media) {
            recovered.media = true
            instance.recoverMediaError()
            return
          }
          setError('load')
        })
        hls = instance
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = playback.url
      } else {
        setError('unsupported')
      }
    })()

    return () => {
      cancelled = true
      hls?.destroy()
    }
  }, [videoId])

  // ----------------------------------------------------------------- gate --

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const onLoadedMetadata = () => {
      if (video.duration && Number.isFinite(video.duration)) setDuration(video.duration)
      const resumeAt = tracking ? Math.max(0, initialPosition - RESUME_REWIND) : 0
      if (resumeAt > 0) {
        snappingBack.current = true
        video.currentTime = resumeAt
      }
    }

    const onPlay = () => {
      spanStart.current = video.currentTime
    }

    const onTimeUpdate = () => {
      const from = spanStart.current
      if (from === null) return
      const played = video.currentTime - from
      // A backwards jump is handled by onSeeked; here only forward progress
      // counts, and only once enough has accumulated to be worth a request.
      if (played >= REPORT_EVERY_SECONDS) flush(video)

      // The frontier may only creep forward with playback. Timeupdate fires
      // several times a second, so ordinary watching always lands inside the
      // tolerance — while a jump lands far outside it and must not count.
      // Without this bound, one successful seek would move the frontier to the
      // end of the video and disable the gate for the rest of the session.
      if (
        video.currentTime > frontier.current &&
        video.currentTime <= frontier.current + SEEK_TOLERANCE
      ) {
        frontier.current = video.currentTime
        setUnlocked(frontier.current)
      }
    }

    /** Where the learner is allowed to be, given what they have watched. */
    const allowed = () => frontier.current + SEEK_TOLERANCE

    // `seeking` is the fast path: catching it here means the frames beyond the
    // frontier are never shown at all.
    const onSeeking = () => {
      if (!preventSeeking || snappingBack.current) return
      if (video.currentTime > allowed()) {
        snappingBack.current = true
        video.currentTime = frontier.current
      }
    }

    // `seeked` is the authoritative one. A write to currentTime during an
    // in-flight seek is not guaranteed to win, so the position is checked again
    // once the browser has settled on it.
    const onSeeked = () => {
      if (preventSeeking && video.currentTime > allowed()) {
        snappingBack.current = true
        video.currentTime = frontier.current
        return // the correction raises its own seeked event
      }
      if (snappingBack.current) {
        snappingBack.current = false
      } else {
        // A legitimate seek: whatever was accumulating belongs to the old
        // position, so close it out before starting a new span here.
        flush(video)
      }
      spanStart.current = video.currentTime
    }

    const onPause = () => flush(video)

    const onEnded = () => {
      flush(video)
      spanStart.current = null
    }

    // Leaving the page mid-lesson is the common case, not the exception.
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush(video)
    }

    video.addEventListener('loadedmetadata', onLoadedMetadata)
    video.addEventListener('play', onPlay)
    video.addEventListener('timeupdate', onTimeUpdate)
    video.addEventListener('seeking', onSeeking)
    video.addEventListener('seeked', onSeeked)
    video.addEventListener('pause', onPause)
    video.addEventListener('ended', onEnded)
    document.addEventListener('visibilitychange', onHidden)

    return () => {
      flush(video)
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      video.removeEventListener('play', onPlay)
      video.removeEventListener('timeupdate', onTimeUpdate)
      video.removeEventListener('seeking', onSeeking)
      video.removeEventListener('seeked', onSeeked)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('ended', onEnded)
      document.removeEventListener('visibilitychange', onHidden)
    }
  }, [flush, initialPosition, preventSeeking, tracking])

  if (error) {
    return (
      <div className="player player--error" role="alert">
        {error === 'unsupported' ? t('videoUnsupported') : t('videoLoadFailed')}
      </div>
    )
  }

  return (
    <div className="player">
      <video ref={videoRef} controls playsInline poster={poster} preload="metadata" aria-label={t('videoLabel')} />
      {preventSeeking && duration > 0 && <WatchedRail duration={duration} frontier={unlocked} />}
    </div>
  )
}

/** Shows how much of the video is unlocked, so the limit is visible rather than
 *  merely felt when a drag springs back. */
function WatchedRail({ duration, frontier }: { duration: number; frontier: number }) {
  const t = useT(learn)
  const pct = Math.max(0, Math.min(100, Math.round((frontier / duration) * 100)))
  return (
    <div className="player__rail" role="img" aria-label={t('unlockedPart', { percent: pct })} title={t('seekHint')}>
      <div className="player__rail-fill" style={{ width: `${pct}%` }} />
    </div>
  )
}
